import { getTranslation } from '@easy-expense/intl-client';
import { MCCIndustries } from '@easy-expense/utils-client';

import { SearchSurvey } from '../../components/OnboardingFlow/Survey/SearchSurvey';
import { SurveyOption, Survey } from '../../components/OnboardingFlow/Survey/Survey';

export const IndustrySurvey: React.FC<{
  onSelect: (options: string[]) => void;
  selectedOptions: string[];
}> = ({ onSelect, selectedOptions }) => {
  const options: { groupHeader: string; groupOptions: SurveyOption[] }[] = MCCIndustries.map(
    (group) => {
      return {
        groupHeader: group.industry_group,
        groupOptions: group.data.map((industry) => {
          return {
            title: industry.industry,
            emoji: industry.emoji,
            searchQueries: industry.search_queries,
          };
        }),
      };
    },
  );

  return (
    <SearchSurvey
      header={getTranslation(`What industry do you work in?`)}
      text={'Select any industries you work in so we can customize your deductions!'}
      options={options}
      multiSelect
      onOptionSelected={(selected: string[]) => onSelect(selected)}
      selectedOptions={selectedOptions}
    />
  );
};

export const TeamSurvey: React.FC<{
  setTeamSurveyOptions: (options: string[]) => void;
  selectedOptions: string[];
}> = ({ setTeamSurveyOptions, selectedOptions }) => {
  return (
    <Survey
      header={getTranslation(`Do you need to track other people's receipts?`)}
      text={getTranslation('Work alone or with your team!')}
      options={[
        {
          title: getTranslation('Just Me'),
        },
        {
          title: getTranslation('Team Tracking'),
          success: getTranslation('Perfect! Employees love how simple Easy Expense is to use.'),
        },
      ]}
      multiSelect={false}
      onOptionSelected={(selected: string[]) => setTeamSurveyOptions(selected)}
      selectedOptions={selectedOptions}
    />
  );
};

export const BusinessRegistrationSurvey: React.FC<{
  setBusinessRegistration: (options: string[]) => void;
  businessRegistration: string[];
  countryCode: string;
}> = ({ setBusinessRegistration, businessRegistration, countryCode }) => {
  const options =
    countryCode === 'CA'
      ? [
          {
            title: getTranslation('Not Registered'),
            key: 'Not Registered',
          },
          {
            title: getTranslation('Sole Proprietorship'),
            key: 'Sole Proprietorship',
          },
          {
            title: getTranslation('Partnership'),
            key: 'Partnership',
          },
          {
            title: getTranslation('Corporation'),
            key: 'Corporation',
          },
        ]
      : [
          {
            title: getTranslation('Not Registered'),
            key: 'Not Registered',
            success: getTranslation('Easy Expense can help you register your LLC for free'),
          },
          {
            title: getTranslation('Sole Proprietorship'),
            key: 'Sole Proprietorship',
          },
          {
            title: getTranslation('LLC'),
            key: 'LLC',
          },
          {
            title: getTranslation('C-Corp'),
            key: 'C-CORP',
          },
          {
            title: getTranslation('S-Corp'),
            key: 'S-CORP',
          },
        ];

  return (
    <Survey
      header={getTranslation(`Have you registered your business?`)}
      text={''}
      options={options}
      multiSelect={false}
      onOptionSelected={(options: string[]) => setBusinessRegistration(options)}
      selectedOptions={businessRegistration}
    />
  );
};

export const UsageSurvey: React.FC<{
  setUsageOptions: (options: string[]) => void;
  selectedOptions: string[];
}> = ({ setUsageOptions, selectedOptions }) => {
  return (
    <Survey
      header={getTranslation(`Why do you need to scan receipts?`)}
      text={getTranslation('Select all that apply so we can ensure the best experience for you!')}
      options={[
        {
          title: getTranslation('Tax Deductions'),
          success: getTranslation(
            'Perfect! Easy Expense has already helped users find over $500M in tax deductions.',
          ),
        },
        {
          title: getTranslation('Business Bookkeeping'),
          success: getTranslation(
            'Perfect! Easy Expense saves 100s of hours per year by automatically reconciling receipts and bank statements.',
          ),
        },
        {
          title: getTranslation('Submit Expense Reports'),
          success: getTranslation(
            'Perfect! Generate professional PDF reports in seconds to get reimbursed faster.',
          ),
        },
        {
          title: getTranslation('Personal Budget'),
        },
      ]}
      multiSelect={true}
      onOptionSelected={(options: string[]) => setUsageOptions(options)}
      selectedOptions={selectedOptions}
    />
  );
};
